import { useEffect, useState } from "react";
import { toast } from "sonner";
import { updateSettings, uploadPhoto, changeAdminPin } from "../lib/api";
import { photoSrc } from "../lib/constants";

export const SettingsEditor = ({ open, settings, onClose, onSaved }) => {
  const [s, setS] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [changingPin, setChangingPin] = useState(false);

  const doChangePin = async () => {
    if (newPin.trim().length < 4) return toast.error("PIN baru minimal 4 karakter");
    setChangingPin(true);
    try {
      await changeAdminPin(curPin, newPin.trim());
      toast.success("PIN admin berhasil diganti");
      setCurPin(""); setNewPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengganti PIN");
    } finally {
      setChangingPin(false);
    }
  };

  useEffect(() => { if (open) setS(settings || {}); }, [open, settings]);
  if (!open) return null;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setService = (i, k, v) => setS((p) => ({ ...p, services: p.services.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const addService = () => setS((p) => ({ ...p, services: [...(p.services || []), { title: "", description: "", icon: "ph-wrench" }] }));
  const delService = (i) => setS((p) => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));
  const setFeature = (i, k, v) => setS((p) => ({ ...p, features: p.features.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const addFeature = () => setS((p) => ({ ...p, features: [...(p.features || []), { title: "", description: "" }] }));
  const delFeature = (i) => setS((p) => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));

  const uploadImg = async (file, key) => {
    if (!file) return;
    try { set(key, await uploadPhoto(file)); toast.success("Gambar diunggah"); }
    catch { toast.error("Gagal unggah gambar"); }
  };

  const save = async () => {
    setSaving(true);
    try { await updateSettings(s); toast.success("Landing page diperbarui"); onSaved(s); }
    catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none";
  const Field = ({ label, k, area }) => (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      {area
        ? <textarea rows={3} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} />
        : <input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} />}
    </div>
  );

  const ImgField = ({ label, k }) => (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      <div className="flex items-center gap-3">
        {s[k] && <img src={photoSrc(s[k])} alt="" className="w-16 h-12 rounded object-cover border border-slate-300" />}
        <label className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg cursor-pointer">
          <i className="ph-bold ph-image" /> Ganti Gambar
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files[0], k)} />
        </label>
      </div>
    </div>
  );

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-auto flex flex-col max-h-[92vh] animate-fade-up" data-testid="settings-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-teal-800 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2"><i className="ph-bold ph-layout text-teal-300" /> Edit Landing Page</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10"><i className="ph-bold ph-x text-xl" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama Perusahaan" k="company_name" />
            <Field label="Slogan / Tagline" k="tagline" />
          </div>
          <ImgField label="Logo Perusahaan (tampil di navbar & laporan)" k="logo_url" />
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Hero (Bagian Atas)</p>
            <Field label="Judul Hero" k="hero_title" />
            <Field label="Sub-judul Hero" k="hero_subtitle" area />
            <ImgField label="Gambar Hero" k="hero_image_url" />
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Tentang</p>
            <Field label="Judul Tentang" k="about_title" />
            <Field label="Teks Tentang" k="about_text" area />
            <ImgField label="Gambar Tentang" k="about_image_url" />
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Layanan</p>
              <button onClick={addService} className="text-xs font-bold text-teal-700"><i className="ph-bold ph-plus-circle" /> Tambah</button>
            </div>
            {(s.services || []).map((srv, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start">
                <input value={srv.icon} onChange={(e) => setService(i, "icon", e.target.value)} className={`${inputCls} col-span-3`} placeholder="ph-wrench" />
                <input value={srv.title} onChange={(e) => setService(i, "title", e.target.value)} className={`${inputCls} col-span-4`} placeholder="Judul" />
                <input value={srv.description} onChange={(e) => setService(i, "description", e.target.value)} className={`${inputCls} col-span-4`} placeholder="Deskripsi" />
                <button onClick={() => delService(i)} className="col-span-1 text-rose-500 py-2"><i className="ph-bold ph-trash" /></button>
              </div>
            ))}
            <p className="text-[10px] text-slate-400">Ikon memakai nama Phosphor (mis. ph-snowflake, ph-lightning, ph-television-simple, ph-wrench).</p>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Keunggulan</p>
              <button onClick={addFeature} className="text-xs font-bold text-teal-700"><i className="ph-bold ph-plus-circle" /> Tambah</button>
            </div>
            {(s.features || []).map((f, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} className={`${inputCls} col-span-5`} placeholder="Judul" />
                <input value={f.description} onChange={(e) => setFeature(i, "description", e.target.value)} className={`${inputCls} col-span-6`} placeholder="Deskripsi" />
                <button onClick={() => delFeature(i)} className="col-span-1 text-rose-500 py-2"><i className="ph-bold ph-trash" /></button>
              </div>
            ))}
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><i className="ph-bold ph-sparkle text-teal-600" /> Model AI (Gemini)</p>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Model untuk Rekomendasi, Ringkasan &amp; Import Excel</label>
              <select value={s.ai_model || "gemini-flash-latest"} onChange={(e) => set("ai_model", e.target.value)} className={`${inputCls} bg-white`} data-testid="ai-model-select">
                <option value="gemini-flash-latest">Gemini Flash — cepat &amp; hemat (disarankan)</option>
                <option value="gemini-pro-latest">Gemini Pro — lebih pintar (butuh paket berbayar)</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1.5">Flash aktif di paket gratis. <span className="font-semibold text-orange-600">Pro memerlukan akun Gemini berbayar</span> — jika kuota gratis, fitur AI akan gagal. Gunakan Flash bila ragu.</p>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Kontak</p>
            <Field label="Alamat" k="address" area />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Titik Peta (koordinat: lat,lng)" k="map_coords" />
              <Field label="Link Google Maps (tombol 'Buka di Maps')" k="maps_url" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="WhatsApp" k="whatsapp" />
              <Field label="Email" k="email" />
              <Field label="Jam Operasional" k="hours" />
            </div>
          </div>
        </div>
        <div className="px-6 pb-2">
          <div className="bg-rose-50/60 p-4 rounded-xl border border-rose-200 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-rose-600 flex items-center gap-1.5"><i className="ph-bold ph-lock-key" /> Ganti PIN Admin</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">PIN Saat Ini</label>
                <input type="password" value={curPin} onChange={(e) => setCurPin(e.target.value)} className={inputCls} data-testid="current-pin" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">PIN Baru (min 6)</label>
                <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} className={inputCls} data-testid="new-pin" />
              </div>
              <button onClick={doChangePin} disabled={changingPin} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg disabled:opacity-60" data-testid="change-pin-btn">
                {changingPin ? "Memproses..." : "Ganti PIN"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500">Setelah diganti, gunakan PIN baru untuk login admin berikutnya.</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg">Tutup</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg disabled:opacity-60" data-testid="settings-save-btn">
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </div>
    </div>
  );
};
